import './qr-scanner.scss';

// import { Html5QrcodeScanner } from 'html5-qrcode';
import { CameraDevice, Html5Qrcode, Html5QrcodeCameraScanConfig } from 'html5-qrcode';

import Component from '../component/component';
// import Select from '../select/select';

// interface IQrConfig {
//     fps: number;
//     qrbox: {
//         width: number;
//         height: number;
//     };
//     aspectRatio: number;
// }

/* eslint-disable */
enum Html5QrcodeSupportedFormats {
    QR_CODE = 0,
    AZTEC,
    CODABAR,
    CODE_39,
    CODE_93,
    CODE_128,
    DATA_MATRIX,
    MAXICODE,
    ITF,
    EAN_13,
    EAN_8,
    PDF_417,
    RSS_14,
    RSS_EXPANDED,
    UPC_A,
    UPC_E,
    UPC_EAN_EXTENSION,
}
/* eslint-enable */

class QrcodeResultFormat {
    public readonly format: Html5QrcodeSupportedFormats;
    public readonly formatName: string;
}

interface IQrcodeResult {
    /** Decoded text. */
    text: string;

    /** Format that was successfully scanned. */
    format?: QrcodeResultFormat,
}

interface IHtml5QrcodeResult {
    decodedText: string;
    result: IQrcodeResult;
}

export default class QrScanner extends Component {
    static initSelector: string = '.js-qr-scanner';

    html5QrCode: Html5Qrcode;

    public scannerContainer: HTMLElement;
    public cameraSelector: HTMLSelectElement;
    public descriptionContainer: HTMLElement;
    private cameraZoomOn: boolean = false;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private cameraSelectorChangeEvent: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private scannerContainerClickEvent: any;

    constructor(element: HTMLElement) {
        super(element);

        this.scannerContainer = this.element.querySelector('.qr-scanner__scanner');
        this.cameraSelector = this.element.querySelector('.qr-scanner__camera-select');
        this.descriptionContainer = this.element.querySelector('.qr-scanner__description');

        if (this.cameraSelector) {
            this.cameraSelectorChangeEvent = this.cameraSelectorChangeHandler.bind(this);
            this.cameraSelector.addEventListener('change', this.cameraSelectorChangeEvent);
        }

        // register event for element click
        this.scannerContainerClickEvent = this.scannerContainerClickHandler.bind(this);
        this.scannerContainer.addEventListener('click', this.scannerContainerClickEvent);

        this.init();
    }

    init(): void {
        const scannerId: string = this.element.querySelector('.qr-scanner__scanner').getAttribute('id');

        this.html5QrCode = new Html5Qrcode(scannerId);


        Html5Qrcode.getCameras().then((devices: CameraDevice[]) => {
            let idOrConfig: string | MediaTrackConstraints = { facingMode: 'environment' };

            // facingMode: 'environment' will select fisheye camera if available
            // acording to this info: https://www.reddit.com/r/javascript/comments/8eg8w5/choosing_cameras_in_javascript_with_the/
            // "normal" environment camera is usually the last camera in the list

            let defaultCamera: CameraDevice = null;

            if (devices) {
                const lastUsedCameraID: string = localStorage.getItem('lastUsedCameraID');

                if (lastUsedCameraID && devices.find((device: CameraDevice) => device.id === lastUsedCameraID)) {
                    defaultCamera = devices.find((device: CameraDevice) => device.id === lastUsedCameraID);
                }
                // for newer devices, the last camera selection is not correct anymore
                // else if (devices.length > 2) { // if there are more than 2 cameras, then expect to have more than 1 environment camera, take the last one
                //     defaultCamera = devices[devices.length - 1];

                //     // if the last camera is a fisheye camera, then take the second to last camera
                //     if (defaultCamera.label.toLowerCase().includes('telephoto')
                //         || defaultCamera.label.toLowerCase().includes('zoom')
                //         || defaultCamera.label.toLowerCase().includes('wide')) {
                //         defaultCamera = devices[devices.length - 2];
                //     }
                // }

                // set camera selector if there are more than 1 camera
                if (devices.length > 1 && this.cameraSelector) {
                    this.cameraSelector.style.display = 'block';

                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const choices: any[] = devices.map((device: CameraDevice) => ({
                        value: device.id,
                        label: device.label,
                    }));

                    // add camera selector options
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const options: any = choices.map((choice: any) => `<option value="${choice.value}">${choice.label}</option>`);

                    this.cameraSelector.innerHTML = options.join('');
                }
            }

            // if the default camera is not the front camera, then use it
            if (defaultCamera) {
                if (!defaultCamera.label.toLowerCase().includes('front')) {
                    idOrConfig = defaultCamera.id;
                }
            } else {
                localStorage.removeItem('lastUsedCameraID');
            }


            this.startScanner(idOrConfig);

            document.addEventListener('stopScanner', this.stopScanner.bind(this));
        }).catch(() => {
            this.element.classList.add('not-allowed');
        });

    }

    // eslint-disable-next-line
    qrCodeSuccessCallback(decodedText: string, decodedResult: IHtml5QrcodeResult): void {
        const customEvent: CustomEvent = new CustomEvent('scanSuccess', {
            detail: {
                decodedText,
                decodedResult,
            },
        });

        this.element.dispatchEvent(customEvent);
    }

    // eslint-disable-next-line
    qrCodeErrorCallback(decodedText: string, decodedResult: any): void {
        /* handle error */
        // Note that this is not really an error,
        // rather just the QR code not currently being in camera viewport
    }

    stopScanner(): void {
        try {
            this.html5QrCode.stop();
        } catch (e) {
            // catch errors when multiple scanners are on the page (but only 1 active)
        }

        // remove camera selector if it's visible
        if (this.cameraSelector && this.cameraSelector.style.display === 'block') {
            this.cameraSelector.innerHTML = '';
            this.cameraSelector.style.display = 'none';
        }

        // remove event listeners
        if (this.cameraSelectorChangeEvent) {
            this.cameraSelector.removeEventListener('change', this.cameraSelectorChangeEvent);
        }

        if (this.scannerContainerClickEvent) {
            this.scannerContainer.removeEventListener('click', this.scannerContainerClickEvent);
        }
    }

    private stopScannerInner(): Promise<void> {
        return this.html5QrCode.stop().then(() => {
            // console.log('QR Code scanning stopped.');
        }).catch(() => {
            console.error('Error stopping the QR code scanner');
        });
    }

    private startScanner(idOrConfig: string | MediaTrackConstraints): void {

        if (this.html5QrCode.isScanning) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const config: Html5QrcodeCameraScanConfig | any = {
            fps: 10,
            qrbox: {
                width: window.screen.width < 600 ? 180 : 300,
                height: window.screen.width < 600 ? 180 : 300,
            },
            aspectRatio: 1,
        };

        this.html5QrCode.start(
            idOrConfig,
            config, this.qrCodeSuccessCallback.bind(this),
            this.qrCodeErrorCallback.bind(this))
            .then(() => {
                const trackSettings: MediaTrackSettings = this.html5QrCode.getRunningTrackSettings();

                this.setCameraSelectorChoice(trackSettings.deviceId); // set the camera selector to the currently active camera

                setTimeout(() => {
                    this.toggleCameraZoom(true); // force zoom after starting the scanner
                }, 1000);
            })
            .catch(() => {
                this.element.classList.add('not-allowed');
            });


    }

    private setCameraSelectorChoice(value: string): void {
        // set value of the camera selector if it's visible
        if (this.cameraSelector && this.cameraSelector.style.display === 'block') {
            // this.cameraSelectorComponent.choices.setChoiceByValue(value);
            this.cameraSelector.value = value;
            // save the last used camera
            localStorage.setItem('lastUsedCameraID', value);
        }
    }

    private toggleCameraZoom(forceZoom: boolean): void {

        if (!this.html5QrCode.isScanning) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        // const scannerCapabilities: any = this.html5QrCode.getRunningTrackCapabilities() as any;

        // if (!scannerCapabilities.zoom) {
        //     return;
        // }

        // no zoom toggle needed if continuous focus mode is available
        // if (scannerCapabilities.focusMode?.includes('continuous')) {
        //     return;
        // }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const settings: any = this.html5QrCode.getRunningTrackSettings() as any;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let newZoom: any = settings.zoom || 1.0;

        if (!this.cameraZoomOn || forceZoom) {
            newZoom = 4.0;
            this.cameraZoomOn = true;
        } else {
            newZoom = 1.0;
            this.cameraZoomOn = false;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const advanced: any = {
            zoom: newZoom,
        };

        // if (scannerCapabilities.focusDistance) {
        //     advanced.focusMode = 'manual';
        //     advanced.focusDistance = 0.03; // in meters
        // }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const constraints: any = {
            focusMode: 'continuous',
            width: { ideal: 1000 },
            height: { ideal: 1000 },
            advanced: [advanced],
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.html5QrCode.applyVideoConstraints(constraints as any);

    }

    private cameraSelectorChangeHandler(event: Event): void {
        const target: HTMLSelectElement = event.target as HTMLSelectElement;

        if (this.html5QrCode.isScanning) {
            this.stopScannerInner().then(() => {
                this.startScanner(target.value);
            });
        } else {
            this.startScanner(target.value);
        }
    }

    private scannerContainerClickHandler(): void {
        this.toggleCameraZoom(false);
    }
}
